import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { Drawer } from '../../app/Drawer';
import { DrawerLink } from '../../app/DrawerLink';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { FavoriteButton } from '../../components/ui/FavoriteButton';
import { SkeletonBlock, SkeletonText } from '../../components/ui/Loader';
import { Verified } from '../../components/ui/Verified';
import { copy } from '../../config/copy';
import { ApiError } from '../../lib/api';
import { useDocumentTitle } from '../../lib/title';
import { usePlayer } from '../../stores/player';
import { AddToPlaylist } from './AddToPlaylist';
import { PlaylistCover } from './PlaylistCover';
import { playlists } from './api';
import { usePlaylistFollow } from './usePlaylist';

/**
 * A playlist, in the side drawer.
 *
 * The listener's view: cover, whose it is, the tracks, one button that plays
 * the lot, and Follow when it is somebody else's. Renaming, reordering and
 * removing stay on the page - they are the owner's tools, and somebody who
 * opened this from a profile came to listen.
 *
 * Its own drawer rather than the album's because the two share a table and
 * not a shape: an album has an artist and a playlist has an owner, an album
 * has a cover and a playlist mostly has other people's.
 */
export function PlaylistDrawer() {
  const { id = '' } = useParams();
  const play = usePlayer((s) => s.play);
  const current = usePlayer((s) => s.track);

  const { data, isPending, error } = useQuery({
    queryKey: ['playlist', Number(id)],
    queryFn: () => playlists.get(id),
    retry: false,
  });

  useDocumentTitle(data?.title ?? null);

  const playable = (data?.tracks ?? []).filter((track) => track.status === 'ready');

  return (
    <Drawer title={data?.title ?? 'Playlist'} fullPath={`/playlists/${id}`}>
      {isPending && (
        <div className="flex flex-col gap-5" aria-busy="true">
          <SkeletonBlock className="aspect-square w-full" />
          <SkeletonText lines={2} />
        </div>
      )}

      {error && (
        <Alert tone="error">
          {error instanceof ApiError ? error.message : 'That playlist could not be loaded.'}
        </Alert>
      )}

      {data && (
        <div className="flex flex-col gap-5" data-drawer-playlist={data.id}>
          <PlaylistCover
            artwork={data.artwork}
            mosaic={data.mosaic}
            rounded="rounded-[var(--radius-panel)]"
            className="aspect-square w-full"
          />

          <div className="flex flex-col gap-1.5">
            <h2 className="text-2xl font-bold leading-tight tracking-tight text-ink">{data.title}</h2>
            {data.owner && (
              <Link to={`/${data.owner.username}`} className="text-[15px] text-text-2 hover:text-ink hover:underline">
                {data.owner.display_name}
                {data.owner.verified && <Verified />}
              </Link>
            )}
            <span className="meta text-faint">
              {data.tracks.length}{' '}
              {data.tracks.length === 1 ? copy['noun.track'].toLowerCase() : copy['noun.track.plural'].toLowerCase()}
              {data.follower_count > 0 &&
                ` · ${data.follower_count} ${data.follower_count === 1 ? 'follower' : 'followers'}`}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {playable.length > 0 && (
              <Button data-drawer-play-playlist onClick={() => void play(playable[0]!, playable, 'collection', { id: data.id, title: data.title })}>
                Play playlist
              </Button>
            )}
            <FollowControl id={data.id} followable={data.followable} followed={data.followed} />
          </div>

          <ul className="flex flex-col gap-1 border-t border-line pt-4" data-drawer-playlist-tracks>
            {data.tracks.map((track, index) => (
              <li key={track.id}>
                <DrawerLink
                  to={`/tracks/${track.id}`}
                  className={`flex items-center gap-3 rounded-[var(--radius-control)] px-2 py-2.5 transition-colors hover:bg-raised ${
                    current?.id === track.id ? 'bg-raised' : ''
                  }`}
                >
                  <span className="w-5 shrink-0 text-right font-mono text-[11px] text-faint">{index + 1}</span>
                  <span className="min-w-0 flex-1 leading-tight">
                    <span className="block truncate text-sm text-ink">{track.title}</span>
                    {track.owner && (
                      <span className="meta block truncate text-faint">{track.owner.display_name}</span>
                    )}
                  </span>
                  <FavoriteButton trackId={track.id} favorited={track.favorited} size="sm" className="shrink-0" />
                  <AddToPlaylist trackId={track.id} title={track.title} size="sm" />
                </DrawerLink>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Drawer>
  );
}

/** Follow or unfollow, on the same keys the page uses, so both surfaces move together. */
function FollowControl({ id, followable, followed }: { id: number; followable: boolean; followed: boolean }) {
  const { follow, unfollow } = usePlaylistFollow(id);

  if (!followable && !followed) return null;

  return (
    <Button
      variant={followed ? 'ghost' : 'secondary'}
      data-drawer-playlist-follow={followed ? 'following' : 'not'}
      loading={follow.isPending || unfollow.isPending}
      onClick={() => (followed ? unfollow.mutate() : follow.mutate())}
    >
      {followed ? 'Unfollow' : 'Follow'}
    </Button>
  );
}
